"use client";

import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { useAuthStore } from "@/store/useAuthStore";
import PushNotificationManager from "@/components/common/PushNotificationManager";
import SocketCallManager from "@/components/common/SocketCallManager";
import { toast } from "sonner";

// ─── Session-expiry deduplication guard ───────────────────────────────────────
// Prevents multiple simultaneous 401 responses (e.g. multiple parallel queries
// all failing at once) from firing the toast and redirect more than one time.
let isHandlingSessionExpiry = false;

const handleError = (error) => {
  // Handle 401 Unauthorized
  if (error?.response?.status === 401) {
    // If we're already handling a session expiry, skip — this is a duplicate
    // 401 from another inflight or cached query on the same page.
    if (isHandlingSessionExpiry) return;
    isHandlingSessionExpiry = true;

    // 1. Log the user out (clears cookies + zustand state)
    useAuthStore.getState().logout();

    // 2. Show a single toast
    toast.error("Session expired. Please login again.", {
      id: "session-expiry",
    });

    // 3. Redirect to login after a brief delay so the toast is visible,
    //    then clear the entire query cache AFTER navigation so the new
    //    login session starts completely fresh.
    setTimeout(() => {
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      // Clear cache after redirect so re-login doesn't replay stale errors
      queryClient.clear();
      // Reset the guard so a future session expiry (after a new login) works again
      isHandlingSessionExpiry = false;
    }, 800);

    return;
  }

  // Default error message (non-401 errors)
  const errorMessage =
    error?.response?.data?.message ||
    error?.message ||
    "An unexpected error occurred";

  toast.error(errorMessage);
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleError,
  }),
  mutationCache: new MutationCache({
    onError: handleError,
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Never retry on 401 — token is expired, retrying is pointless
        if (error?.response?.status === 401) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      // Do NOT use stale cache for pages that require auth —
      // force a fresh fetch after login to avoid showing 401-cached results
      staleTime: 0,
    },
    mutations: {
      retry: (failureCount, error) => {
        if (error?.response?.status === 401) return false;
        return failureCount < 1;
      },
    },
  },
});

const Providers = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <PushNotificationManager />
      <SocketCallManager />
      {children}
    </QueryClientProvider>
  );
};

export default Providers;
